import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setSelectedEvent, setViewingUserEvents } from '../../store/Actions';
import { FancyButton, FancyIconButton } from '../common/buttons';
import { FancySelect, FancyAutocomplete } from '../common/inputs';
import EmojiPicker from 'emoji-picker-react';
import Menu from '@mui/material/Menu';
import Popover from '@mui/material/Popover';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import HistoryIcon from '@mui/icons-material/History';
import { PlaceSearch } from '../common/PlaceSearch';
import MyDateTimePicker from '../common/MyDateTimePicker';
import { RichTextEditor } from '../common/richtexteditor/RichTextEditor';
import Cropper from 'react-easy-crop';
import { HexColorPicker } from "react-colorful";
import { deleteUserEvent, storeUserEvent } from '../../firebase/Firestore';
import { storeFile } from '../../firebase/CloudStorage';
import { buildTripSummaries } from '../../utils/EntitySummaries';
import './EditEvent.scss';

const COVER_ASPECT = 400 / 310;
const COVER_MIN_ZOOM = 0.2;
const COVER_MAX_ZOOM = 5;
const COVER_ZOOM_STEP = 0.1;
const COVER_THUMB_LIMIT = 5;
const DRAFT_KEY_PREFIX = 'lifeEvents.editEventDraft.';
const DRAFT_AUTOSAVE_DEBOUNCE_MS = 600;

const createImage = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
});

const getCroppedCoverBlob = async (imageSrc, croppedAreaPixels, backgroundColor) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    const context = canvas.getContext('2d');
    context.fillStyle = backgroundColor || '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const sourceX = Math.max(0, croppedAreaPixels.x);
    const sourceY = Math.max(0, croppedAreaPixels.y);
    const sourceWidth = Math.min(
        image.naturalWidth - sourceX,
        croppedAreaPixels.width + Math.min(0, croppedAreaPixels.x)
    );
    const sourceHeight = Math.min(
        image.naturalHeight - sourceY,
        croppedAreaPixels.height + Math.min(0, croppedAreaPixels.y)
    );

    if(sourceWidth <= 0 || sourceHeight <= 0){
        return await new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/jpeg', 0.95);
        });
    }

    context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        sourceX - croppedAreaPixels.x,
        sourceY - croppedAreaPixels.y,
        sourceWidth,
        sourceHeight
    );

    return await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95);
    });
}

// Sample dominant colors from a (CORS-friendly) image. Returns up to `count`
// hex strings ordered by frequency. Silently returns [] if CORS prevents
// reading the canvas data, which is fine — the UI just hides the strip.
const extractDominantColors = async (url, count = 5) => {
    try {
        const image = await createImage(url);
        const W = 80;
        const H = Math.max(1, Math.round(W * (image.naturalHeight / image.naturalWidth)));
        const canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, W, H);
        const data = context.getImageData(0, 0, W, H).data;
        const buckets = new Map();
        for(let i = 0; i < data.length; i += 4){
            const a = data[i + 3];
            if(a < 200) continue;
            const r = data[i] & 0xE0;
            const g = data[i + 1] & 0xE0;
            const b = data[i + 2] & 0xE0;
            const key = (r << 16) | (g << 8) | b;
            buckets.set(key, (buckets.get(key) || 0) + 1);
        }
        const sorted = [...buckets.entries()].sort((a, b) => b[1] - a[1]);
        return sorted.slice(0, count).map(([key]) => {
            const r = (key >> 16) & 0xFF;
            const g = (key >> 8) & 0xFF;
            const b = key & 0xFF;
            return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
        });
    } catch (error) {
        return [];
    }
};

const storeFileAsync = (filename, file) => new Promise((resolve, reject) => {
    storeFile(filename, file, resolve, null, reject);
});

const isValidDateValue = (value) => value instanceof Date && !Number.isNaN(value.getTime());
const selectedIdsAreValid = (ids, optionsById) => ids.every((id) => Boolean(optionsById[id]));
const locationIsValid = (value) => Boolean(
    value &&
    value.description &&
    value.placeId &&
    value.coordinates &&
    Number.isFinite(value.coordinates.lat) &&
    Number.isFinite(value.coordinates.lng)
);

const numbersAreClose = (a, b) => Math.abs((a ?? 0) - (b ?? 0)) < 0.001;
const clampCoverZoom = (zoom) => Math.min(COVER_MAX_ZOOM, Math.max(COVER_MIN_ZOOM, zoom));
const cropValuesAreEqual = (a, b) => Boolean(
    a &&
    b &&
    numbersAreClose(a.x, b.x) &&
    numbersAreClose(a.y, b.y)
);
const areaValuesAreEqual = (a, b) => Boolean(
    a &&
    b &&
    numbersAreClose(a.x, b.x) &&
    numbersAreClose(a.y, b.y) &&
    numbersAreClose(a.width, b.width) &&
    numbersAreClose(a.height, b.height)
);

// Walks the rich-text JSON tree and returns the `src` of every image node.
const extractDescriptionImageUrls = (descriptionJSON) => {
    if(!descriptionJSON || typeof descriptionJSON !== 'object') return [];
    const urls = [];
    const walk = (node) => {
        if(!node) return;
        if(node.type === 'image' && node.attrs && node.attrs.src){
            urls.push(node.attrs.src);
        }
        if(Array.isArray(node.content)){
            node.content.forEach(walk);
        }
    };
    walk(descriptionJSON);
    return urls;
};

const sortTripsByMostRecent = (trips, tripSummaries) => {
    return Object.keys(trips || {}).sort((a, b) => {
        const aEndAt = tripSummaries[a]?.endAt;
        const bEndAt = tripSummaries[b]?.endAt;

        if(aEndAt && bEndAt) return bEndAt - aEndAt;
        if(aEndAt) return -1;
        if(bEndAt) return 1;
        return (trips[a]?.name || '').localeCompare(trips[b]?.name || '');
    });
}

// Date helpers used by the quick-pick chips. All return Date objects with the
// time zeroed out so they round-trip cleanly through the date-only state.
const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};
const dateToday = () => startOfDay(new Date());
const dateYesterday = () => {
    const d = dateToday();
    d.setDate(d.getDate() - 1);
    return d;
};
const dateThisOrNextSaturday = () => {
    const d = dateToday();
    const day = d.getDay(); // 0=Sun..6=Sat
    const offset = (6 - day + 7) % 7;
    d.setDate(d.getDate() + offset);
    return d;
};

// Draft serialization: only persist what's safely serializable. Dates are
// stored as ISO strings; descriptionJSON is already a plain object. The cover
// blob URL stays out — it's a transient result of the upload pipeline.
const serializeDraft = (snap) => ({
    ...snap,
    startDate: snap.startDate ? snap.startDate.toISOString() : null,
    startTime: snap.startTime ? snap.startTime.toISOString() : null,
    endDate: snap.endDate ? snap.endDate.toISOString() : null,
    endTime: snap.endTime ? snap.endTime.toISOString() : null,
    savedAt: Date.now(),
});

const deserializeDraft = (raw) => {
    if(!raw) return null;
    try {
        const data = JSON.parse(raw);
        return {
            ...data,
            startDate: data.startDate ? new Date(data.startDate) : null,
            startTime: data.startTime ? new Date(data.startTime) : null,
            endDate: data.endDate ? new Date(data.endDate) : null,
            endTime: data.endTime ? new Date(data.endTime) : null,
        };
    } catch (e) {
        return null;
    }
};

const formatRelativeTime = (timestamp) => {
    if(!timestamp) return '';
    const diff = Date.now() - timestamp;
    if(diff < 60_000) return 'just now';
    if(diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
    if(diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return new Date(timestamp).toLocaleString();
};

export function EditEvent() {
    const fileInputRef = useRef();
    const titleInputRef = useRef(null);
    const dialogData = useSelector(state=>state.dialogData);
    const userEvents = useSelector(state=>state.viewingUserEvents);
    const userCategories = useSelector(state=>state.viewingUserCategories);
    const userFriends = useSelector(state=>state.viewingUserFriends);
    const userTrips = useSelector(state=>state.viewingUserTrips);
    const userTags = useSelector(state=>state.viewingUserTags);
    const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

    const [coverPicUrl, setCoverPicUrl] = useState(dialogData.event.coverPicUrl??null);
    const [coverPicOriginalUrl, setCoverPicOriginalUrl] = useState(dialogData.event.coverPicOriginalUrl ?? dialogData.event.coverPicUrl ?? null);
    const [coverStorageId, setCoverStorageId] = useState(dialogData.event.coverStorageId ?? dialogData.event.id ?? `${dialogData.event.userId}_${Date.now()}`);
    const coverOptions = dialogData.event.coverPicOptions;
    const [coverCrop, setCoverCrop] = useState(coverOptions && coverOptions.crop ? coverOptions.crop : { x: 0, y: 0 });
    const [coverZoom, setCoverZoom] = useState(coverOptions && coverOptions.zoom ? coverOptions.zoom : 1);
    const [coverCroppedArea, setCoverCroppedArea] = useState(coverOptions && coverOptions.croppedArea ? coverOptions.croppedArea : null);
    const [coverCroppedAreaPixels, setCoverCroppedAreaPixels] = useState(coverOptions && coverOptions.croppedAreaPixels ? coverOptions.croppedAreaPixels : null);
    const [coverBackgroundColor, setCoverBackgroundColor] = useState(coverOptions && coverOptions.backgroundColor ? coverOptions.backgroundColor : '#ffffff');
    const [coverDirty, setCoverDirty] = useState(false);
    const [coverDominantColors, setCoverDominantColors] = useState([]);
    const [title, setTitle] = useState(dialogData.event.title);
    const [location, setLocation] = useState(dialogData.event.location);
    const [startDate, setStartDate] = useState(dialogData.event.startDate ? (dialogData.event.startDate instanceof Date ? dialogData.event.startDate : dialogData.event.startDate.toDate()) : null);
    const [startTime, setStartTime] = useState(dialogData.event.startTime ? (dialogData.event.startTime instanceof Date ? dialogData.event.startTime : dialogData.event.startTime.toDate()) : null);
    const [endDate, setEndDate] = useState(dialogData.event.endDate ? (dialogData.event.endDate instanceof Date ? dialogData.event.endDate : dialogData.event.endDate.toDate()) : null);
    const [endTime, setEndTime] = useState(dialogData.event.endTime ? (dialogData.event.endTime instanceof Date ? dialogData.event.endTime : dialogData.event.endTime.toDate()) : null);
    const [categoryId, setCategoryId] = useState(dialogData.event.categoryId);
    const [emoji, setEmoji] = useState(dialogData.event.emoji ?? userCategories[categoryId].defaultEmoji);
    const [friendIds, setFriendIds] = useState(dialogData.event.friendIds ?? []);
    const [tripIds, setTripIds] = useState(dialogData.event.tripIds ?? []);
    const [tagIds, setTagIds] = useState(dialogData.event.tagIds ?? []);
    const [descriptionHTML, setDescriptionHTML] = useState(dialogData.event.descriptionHTML ?? '');
    const [descriptionJSON, setDescriptionJSON] = useState(dialogData.event.descriptionJSON ?? {});
    const [validationErrors, setValidationErrors] = useState({});
    const [pickerErrors, setPickerErrors] = useState({});
    const [coverUploadStatus, setCoverUploadStatus] = useState('idle');
    const [hasDescriptionUploads, setHasDescriptionUploads] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [eventPhotoMenuAnchor, setEventPhotoMenuAnchor] = useState(null);

    // New UX state
    const [isDirty, setIsDirty] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [showEnd, setShowEnd] = useState(Boolean(dialogData.event.endDate || dialogData.event.endTime));
    const [isAllDay, setIsAllDay] = useState(!dialogData.event.startTime && !dialogData.event.endTime);
    const [isDragging, setIsDragging] = useState(false);
    const [coverExplicitlyCleared, setCoverExplicitlyCleared] = useState(false);
    const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
    const [peopleTab, setPeopleTab] = useState(0);
    const [draftBanner, setDraftBanner] = useState(null); // { savedAt, draft }

    const dispatch = useDispatch();
    const dialogTitle = dialogData.dialogTitle || (dialogData.event.id ? 'Edit Event' : 'New Event');
    const draftKey = useMemo(
        () => `${DRAFT_KEY_PREFIX}${dialogData.event.id || 'new'}`,
        [dialogData.event.id]
    );
    const tripSummaries = useMemo(() => buildTripSummaries(userEvents), [userEvents]);
    const tripOptions = useMemo(() => sortTripsByMostRecent(userTrips, tripSummaries), [userTrips, tripSummaries]);
    const descriptionImageUrls = useMemo(
        () => extractDescriptionImageUrls(descriptionJSON),
        [descriptionJSON]
    );

    const markDirty = useCallback(() => setIsDirty(true), []);

    // ─── Draft restore on mount ───
    useEffect(() => {
        try {
            const raw = localStorage.getItem(draftKey);
            const parsed = deserializeDraft(raw);
            if(parsed) setDraftBanner(parsed);
        } catch (e) { /* localStorage unavailable, no banner */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Auto-save draft (debounced) ───
    useEffect(() => {
        if(!isDirty) return undefined;
        const handle = setTimeout(() => {
            try {
                const snap = {
                    title, location,
                    startDate, startTime, endDate, endTime,
                    categoryId, emoji,
                    friendIds, tripIds, tagIds,
                    descriptionHTML, descriptionJSON,
                    coverPicOriginalUrl, coverStorageId,
                    coverCrop, coverZoom, coverBackgroundColor,
                    coverCroppedArea, coverCroppedAreaPixels,
                    isAllDay, showEnd,
                };
                localStorage.setItem(draftKey, JSON.stringify(serializeDraft(snap)));
            } catch (e) { /* quota/disabled, just skip */ }
        }, DRAFT_AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [
        isDirty, draftKey,
        title, location,
        startDate, startTime, endDate, endTime,
        categoryId, emoji,
        friendIds, tripIds, tagIds,
        descriptionHTML, descriptionJSON,
        coverPicOriginalUrl, coverStorageId,
        coverCrop, coverZoom, coverBackgroundColor,
        coverCroppedArea, coverCroppedAreaPixels,
        isAllDay, showEnd,
    ]);

    const restoreDraft = () => {
        if(!draftBanner) return;
        const d = draftBanner;
        if(d.title !== undefined) setTitle(d.title);
        if(d.location !== undefined) setLocation(d.location);
        if(d.startDate !== undefined) setStartDate(d.startDate);
        if(d.startTime !== undefined) setStartTime(d.startTime);
        if(d.endDate !== undefined) setEndDate(d.endDate);
        if(d.endTime !== undefined) setEndTime(d.endTime);
        if(d.categoryId) setCategoryId(d.categoryId);
        if(d.emoji) setEmoji(d.emoji);
        if(Array.isArray(d.friendIds)) setFriendIds(d.friendIds);
        if(Array.isArray(d.tripIds)) setTripIds(d.tripIds);
        if(Array.isArray(d.tagIds)) setTagIds(d.tagIds);
        if(d.descriptionHTML !== undefined) setDescriptionHTML(d.descriptionHTML);
        if(d.descriptionJSON !== undefined) setDescriptionJSON(d.descriptionJSON);
        if(d.coverPicOriginalUrl !== undefined) setCoverPicOriginalUrl(d.coverPicOriginalUrl);
        if(d.coverStorageId) setCoverStorageId(d.coverStorageId);
        if(d.coverCrop) setCoverCrop(d.coverCrop);
        if(typeof d.coverZoom === 'number') setCoverZoom(d.coverZoom);
        if(d.coverBackgroundColor) setCoverBackgroundColor(d.coverBackgroundColor);
        if(d.coverCroppedArea) setCoverCroppedArea(d.coverCroppedArea);
        if(d.coverCroppedAreaPixels) setCoverCroppedAreaPixels(d.coverCroppedAreaPixels);
        if(typeof d.isAllDay === 'boolean') setIsAllDay(d.isAllDay);
        if(typeof d.showEnd === 'boolean') setShowEnd(d.showEnd);
        markDirty();
        setDraftBanner(null);
    };

    const discardDraft = () => {
        try { localStorage.removeItem(draftKey); } catch (e) { /* ignore */ }
        setDraftBanner(null);
    };

    // ─── Auto-focus the title field for new events ───
    useEffect(() => {
        if(dialogData.event.id) return;
        const t = setTimeout(() => titleInputRef.current?.focus(), 120);
        return () => clearTimeout(t);
    }, [dialogData.event.id]);

    // ─── Default cover from first description image ───
    useEffect(() => {
        if(coverPicOriginalUrl) return;
        if(coverExplicitlyCleared) return;
        if(descriptionImageUrls.length === 0) return;
        const firstUrl = descriptionImageUrls[0];
        setCoverPicOriginalUrl(firstUrl);
        setCoverPicUrl(null);
        setCoverCrop({ x: 0, y: 0 });
        setCoverZoom(1);
        setCoverCroppedArea(null);
        setCoverCroppedAreaPixels(null);
        setCoverBackgroundColor('#ffffff');
        setCoverDirty(true);
    }, [descriptionImageUrls, coverPicOriginalUrl, coverExplicitlyCleared]);

    // ─── Sample dominant colors when the cover image changes ───
    useEffect(() => {
        if(!coverPicOriginalUrl){ setCoverDominantColors([]); return; }
        let cancelled = false;
        extractDominantColors(coverPicOriginalUrl, 5).then((colors) => {
            if(!cancelled) setCoverDominantColors(colors);
        });
        return () => { cancelled = true; };
    }, [coverPicOriginalUrl]);

    const validateEvent = () => {
        const errors = {};
        if(!title || !title.trim()) errors.title = 'Title is required';
        if(!userCategories[categoryId]) errors.category = 'Choose a valid category';
        if(pickerErrors.start) errors.startDate = 'Enter a valid start';
        else if(!isValidDateValue(startDate)) errors.startDate = 'Start date is required';
        if(pickerErrors.end) errors.endDate = 'Enter a valid end';
        else if(endDate && !isValidDateValue(endDate)) errors.endDate = 'Enter a valid end';
        if(!isAllDay && endTime && !endDate) errors.endDate = 'End date is required when end time is set';

        if(isValidDateValue(startDate) && isValidDateValue(endDate)){
            const startDateTime = new Date(startDate);
            if(!isAllDay && isValidDateValue(startTime)){
                startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
            }
            const endDateTime = new Date(endDate);
            if(!isAllDay && isValidDateValue(endTime)){
                endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
            }
            if(endDateTime < startDateTime) errors.endDate = 'End must be after start';
        }

        if(!locationIsValid(location)) errors.location = 'Choose a valid location';
        if(!selectedIdsAreValid(friendIds, userFriends)) errors.friends = 'Remove unavailable friends';
        if(!selectedIdsAreValid(tripIds, userTrips)) errors.trips = 'Remove unavailable trips';
        if(!selectedIdsAreValid(tagIds, userTags)) errors.tags = 'Remove unavailable tags';
        if(coverUploadStatus === 'uploading') errors.uploads = 'Wait for the cover photo to finish uploading';
        if(hasDescriptionUploads) errors.uploads = 'Wait for media uploads to finish';
        return errors;
    }

    const errorCount = useMemo(
        () => Object.values(validationErrors).filter(Boolean).length,
        [validationErrors]
    );

    const saveEvent = async () => {
        if(isSaving) return;
        const nextValidationErrors = validateEvent();
        setValidationErrors(nextValidationErrors);
        if(Object.values(nextValidationErrors).some(Boolean)){
            return;
        }
        setIsSaving(true);
        try {
            let nextCoverPicUrl = coverPicUrl;
            if(coverPicOriginalUrl && coverCroppedAreaPixels && (coverDirty || !nextCoverPicUrl)){
                const croppedCoverBlob = await getCroppedCoverBlob(coverPicOriginalUrl, coverCroppedAreaPixels, coverBackgroundColor);
                nextCoverPicUrl = await storeFileAsync(`${coverStorageId}_CoverPic`, croppedCoverBlob);
                // Cache-buster: the Storage path is stable across edits, so the URL
                // string would otherwise be identical to the previous cover and
                // browsers would keep showing the cached image until a hard refresh.
                nextCoverPicUrl = `${nextCoverPicUrl}&t=${Date.now()}`;
                setCoverPicUrl(nextCoverPicUrl);
            }

            // All-day events store NO times — preserves the existing schema
            // semantics where a missing startTime/endTime means "all day".
            const persistedStartTime = isAllDay ? null : startTime;
            const persistedEndTime = isAllDay ? null : endTime;

            let event = {
                ...dialogData.event,
                coverPicUrl: nextCoverPicUrl,
                coverPicOriginalUrl,
                coverStorageId,
                coverPicOptions: coverPicOriginalUrl ? {
                    crop: coverCrop,
                    zoom: coverZoom,
                    backgroundColor: coverBackgroundColor,
                    croppedArea: coverCroppedArea,
                    croppedAreaPixels: coverCroppedAreaPixels
                } : null,
                title,
                startDate,
                startTime: persistedStartTime,
                endDate,
                endTime: persistedEndTime,
                categoryId,
                emoji,
                friendIds,
                tripIds,
                tagIds,
                descriptionHTML,
                descriptionJSON,
                location
            }
            event = await storeUserEvent(event.id, event);
            // Build a NEW map reference so every useSelector(state =>
            // state.viewingUserEvents) consumer (TripsView, FriendsView,
            // TagsView, CategoriesView, AboutView, the detail views, etc.)
            // detects the change and rerenders. Mutating the existing object
            // in place keeps the same reference and those views go stale
            // until something else forces a rerender.
            dispatch(setViewingUserEvents({ ...userEvents, [event.id]: event }));
            dispatch(setSelectedEvent(event));
            try { localStorage.removeItem(draftKey); } catch (e) { /* ignore */ }
            dispatch(setDialogOpen(''));
        } catch (error) {
            setValidationErrors({
                ...nextValidationErrors,
                uploads: 'Upload failed. Please try again before saving.'
            });
        } finally {
            setIsSaving(false);
        }
    }

    const performDelete = () => {
        deleteUserEvent(dialogData.event.id);
        // New map reference — see the note in saveEvent. Without this,
        // entity/detail views that subscribe to viewingUserEvents directly
        // would keep showing the deleted event until next render.
        const nextEvents = { ...userEvents };
        delete nextEvents[dialogData.event.id];
        dispatch(setViewingUserEvents(nextEvents));
        try { localStorage.removeItem(draftKey); } catch (e) { /* ignore */ }
        dispatch(setDialogOpen(''));
    }

    const onCancel = () => {
        if(isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
        // The draft remains in localStorage so the next mount can offer to
        // restore it. If the user explicitly cancels with no dirty state we
        // leave the existing draft alone (it's likely older than this session).
        if(dialogData.event.id) dispatch(setSelectedEvent(dialogData.event));
        dispatch(setDialogOpen(''));
    }

    // Refs to latest closures, so the global keydown listener never goes stale.
    const saveRef = useRef();
    const cancelRef = useRef();
    saveRef.current = saveEvent;
    cancelRef.current = onCancel;

    useEffect(() => {
        const handler = (e) => {
            if((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')){
                e.preventDefault();
                saveRef.current?.();
                return;
            }
            if(e.key === 'Escape'){
                if(document.querySelector('.MuiPopover-root, .MuiMenu-root')) return;
                cancelRef.current?.();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // Cover photo file handling — used by file input, drag-drop, and clipboard paste.
    const handleCoverFile = useCallback((file) => {
        if(!file) return;
        if(!file.type || !file.type.startsWith('image/')) return;
        const filename = coverStorageId + "_CoverPicOriginal";
        setCoverUploadStatus('uploading');
        setCoverExplicitlyCleared(false);
        storeFile(filename, file, (url) => {
            // Cache-buster: the Storage path is stable, so re-uploading the
            // original would resolve to the same URL string and the browser
            // would keep serving the previously cached image.
            setCoverPicOriginalUrl(`${url}&t=${Date.now()}`);
            setCoverPicUrl(null);
            setCoverCrop({ x: 0, y: 0 });
            setCoverZoom(1);
            setCoverCroppedArea(null);
            setCoverCroppedAreaPixels(null);
            setCoverBackgroundColor('#ffffff');
            setCoverDirty(true);
            setCoverUploadStatus('idle');
            markDirty();
            setValidationErrors((prev) => prev.uploads ? {...prev, uploads: null} : prev);
        }, null, () => {
            setCoverUploadStatus('error');
            setValidationErrors((prev) => ({...prev, uploads: 'Cover photo upload failed. Please try again.'}));
        });
    }, [coverStorageId, markDirty]);

    const onSelectCoverPic = (event) => {
        const file = event.currentTarget.files[0];
        event.currentTarget.value = '';
        handleCoverFile(file);
    }

    const onDropCover = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer?.files?.[0];
        if(file) handleCoverFile(file);
    }

    // ─── Clipboard paste → cover ───
    useEffect(() => {
        const handler = (e) => {
            // Skip when the user is pasting into a contenteditable / rich-text
            // editor — that paste belongs to the editor.
            const target = e.target;
            if(target && (target.isContentEditable || target.closest?.('[contenteditable="true"]'))) return;
            const items = e.clipboardData?.items;
            if(!items) return;
            for(const item of items){
                if(item.kind === 'file' && item.type?.startsWith('image/')){
                    const file = item.getAsFile();
                    if(file){
                        e.preventDefault();
                        handleCoverFile(file);
                        return;
                    }
                }
            }
        };
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }, [handleCoverFile]);

    const onSelectFromEventPhotos = (url) => {
        setEventPhotoMenuAnchor(null);
        setCoverPicOriginalUrl(url);
        setCoverPicUrl(null);
        setCoverCrop({ x: 0, y: 0 });
        setCoverZoom(1);
        setCoverCroppedArea(null);
        setCoverCroppedAreaPixels(null);
        setCoverBackgroundColor('#ffffff');
        setCoverDirty(true);
        setCoverExplicitlyCleared(false);
        markDirty();
        if(validationErrors.uploads) setValidationErrors({...validationErrors, uploads: null});
    }

    const clearCoverPic = () => {
        setCoverPicOriginalUrl(null);
        setCoverPicUrl(null);
        setCoverCrop({ x: 0, y: 0 });
        setCoverZoom(1);
        setCoverCroppedArea(null);
        setCoverCroppedAreaPixels(null);
        setCoverBackgroundColor('#ffffff');
        setCoverDirty(true);
        setCoverExplicitlyCleared(true);
        setShowBackgroundPicker(false);
        markDirty();
    }

    const uploadBlockMessage = coverUploadStatus === 'uploading'
        ? 'Cover photo is still uploading'
        : hasDescriptionUploads
            ? 'Media is still uploading'
            : validationErrors.uploads;
    const saveIsBlocked = coverUploadStatus === 'uploading' || hasDescriptionUploads || isSaving;

    const onCoverCropChange = useCallback((nextCrop) => {
        setCoverCrop((previousCrop) => {
            if(cropValuesAreEqual(previousCrop, nextCrop)) return previousCrop;
            setCoverDirty(true);
            setIsDirty(true);
            return nextCrop;
        });
    }, []);
    const onCoverCropComplete = useCallback((nextCroppedArea, nextCroppedAreaPixels) => {
        setCoverCroppedArea((previousArea) => areaValuesAreEqual(previousArea, nextCroppedArea) ? previousArea : nextCroppedArea);
        setCoverCroppedAreaPixels((previousAreaPixels) => areaValuesAreEqual(previousAreaPixels, nextCroppedAreaPixels) ? previousAreaPixels : nextCroppedAreaPixels);
    }, []);
    const onCoverZoomChange = useCallback((nextZoom) => {
        const clampedZoom = clampCoverZoom(nextZoom);
        setCoverZoom((previousZoom) => {
            if(numbersAreClose(previousZoom, clampedZoom)) return previousZoom;
            setCoverDirty(true);
            setIsDirty(true);
            return clampedZoom;
        });
    }, []);
    const adjustCoverZoom = useCallback((amount) => {
        onCoverZoomChange(coverZoom + amount);
    }, [coverZoom, onCoverZoomChange]);
    const resetCoverCrop = useCallback(() => {
        setCoverCrop({ x: 0, y: 0 });
        onCoverZoomChange(1);
        setCoverDirty(true);
        markDirty();
    }, [onCoverZoomChange, markDirty]);

    // ─── Date/time setters ───
    const onStartChange = ({ date, time }) => {
        setStartDate(date);
        if(!isAllDay) setStartTime(time);
        markDirty();
        if(validationErrors.startDate || validationErrors.endDate){
            setValidationErrors({...validationErrors, startDate: null, endDate: null});
        }
    };
    const onEndChange = ({ date, time }) => {
        setEndDate(date);
        if(!isAllDay) setEndTime(time);
        markDirty();
        if(validationErrors.endDate){
            setValidationErrors({...validationErrors, endDate: null});
        }
    };
    const toggleAllDay = (next) => {
        setIsAllDay(next);
        if(next){
            setStartTime(null);
            setEndTime(null);
        }
        markDirty();
    };
    const removeEnd = () => {
        setEndDate(null);
        setEndTime(null);
        setShowEnd(false);
        if(validationErrors.endDate){
            setValidationErrors({...validationErrors, endDate: null});
        }
        markDirty();
    };

    // ─── Quick-pick chips ───
    const setQuickToday = () => {
        setStartDate(dateToday());
        markDirty();
    };
    const setQuickYesterday = () => {
        setStartDate(dateYesterday());
        markDirty();
    };
    const setQuickWeekend = () => {
        const sat = dateThisOrNextSaturday();
        const sun = new Date(sat);
        sun.setDate(sat.getDate() + 1);
        setStartDate(sat);
        setEndDate(sun);
        setShowEnd(true);
        markDirty();
    };

    // Cover suggestion strip (first N description images)
    const coverSuggestions = useMemo(
        () => descriptionImageUrls.slice(0, COVER_THUMB_LIMIT),
        [descriptionImageUrls]
    );
    const hasMoreCoverOptions = descriptionImageUrls.length > COVER_THUMB_LIMIT;

    // People & tags counts for tab badges
    const peopleCounts = [friendIds.length, tripIds.length, tagIds.length];

    return (
        <div className="ee-page">
            {/* ───────── Sticky top bar ───────── */}
            <header className="ee-topbar">
                <div className="ee-topbar__left">
                    <FancyButton variant="ghost" onClick={onCancel} startIcon={<CloseIcon fontSize="small" />}>
                        Cancel
                    </FancyButton>
                </div>
                <div className="ee-topbar__center">
                    <h1 className="ee-topbar__title">{dialogTitle}</h1>
                    {isDirty && (
                        <span className="ee-topbar__dirty" title="You have unsaved changes" aria-label="Unsaved changes">
                            <span className="ee-topbar__dirty-dot" />
                            Unsaved
                        </span>
                    )}
                </div>
                <div className="ee-topbar__right">
                    {errorCount > 0 && (
                        <span className="ee-topbar__errors" role="status">
                            {errorCount} {errorCount === 1 ? 'issue' : 'issues'} to fix
                        </span>
                    )}
                    <FancyButton
                        variant="dark"
                        onClick={saveEvent}
                        loading={saveIsBlocked}
                        disabled={saveIsBlocked}
                        title="Save event (⌘S)"
                    >
                        Save event
                    </FancyButton>
                </div>
            </header>

            {/* ───────── Scrollable body ───────── */}
            <div className="ee-body">
                {/* Draft restore banner */}
                {draftBanner && (
                    <div className="ee-draft-banner" role="status">
                        <HistoryIcon className="ee-draft-banner__icon" />
                        <div className="ee-draft-banner__text">
                            <strong>Draft found</strong>
                            <span> — saved {formatRelativeTime(draftBanner.savedAt)}</span>
                        </div>
                        <FancyButton variant="ghost" size="sm" onClick={discardDraft}>Discard</FancyButton>
                        <FancyButton variant="dark" size="sm" onClick={restoreDraft}>Restore</FancyButton>
                    </div>
                )}

                {/* HERO: emoji + title + category */}
                <section className="ee-hero">
                    <button
                        className="ee-emoji-tile"
                        onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                        type="button"
                        aria-label="Choose event emoji"
                        title="Click to change emoji"
                    >
                        <span className="ee-emoji-tile__char">{emoji}</span>
                        <span className="ee-emoji-tile__overlay" aria-hidden="true">
                            <EditOutlinedIcon fontSize="inherit" />
                        </span>
                    </button>
                    <Menu
                        anchorEl={emojiAnchorEl}
                        open={Boolean(emojiAnchorEl)}
                        onClose={() => setEmojiAnchorEl(null)}
                    >
                        <EmojiPicker
                            previewConfig={{showPreview: false}}
                            onEmojiClick={(picked) => {
                                setEmoji(picked.emoji);
                                setEmojiAnchorEl(null);
                                markDirty();
                            }}
                        />
                    </Menu>

                    <div className="ee-hero__fields">
                        <input
                            ref={titleInputRef}
                            className={`ee-title-input ${validationErrors.title ? 'has-error' : ''}`}
                            placeholder="Untitled event"
                            value={title || ''}
                            onChange={(e) => {
                                setTitle(e.target.value);
                                markDirty();
                                if(validationErrors.title) setValidationErrors({...validationErrors, title: null});
                            }}
                            aria-label="Event title"
                        />
                        {validationErrors.title && (
                            <div className="ee-field-error">{validationErrors.title}</div>
                        )}
                        <div className="ee-hero__category">
                            <FancySelect
                                label="Category"
                                fullWidth
                                value={categoryId}
                                error={Boolean(validationErrors.category)}
                                helperText={validationErrors.category || ''}
                                onChange={(value) => {
                                    const selectedCategory = userCategories[value];
                                    setEmoji(selectedCategory.defaultEmoji);
                                    setCategoryId(selectedCategory.id);
                                    markDirty();
                                    if(validationErrors.category) setValidationErrors({...validationErrors, category: null});
                                }}
                                options={Object.values(userCategories).map((category) => ({
                                    value: category.id,
                                    label: category.name,
                                    icon: <span style={{ fontSize: 16 }}>{category.defaultEmoji}</span>,
                                }))}
                            />
                        </div>
                    </div>
                </section>

                {/* MAIN GRID: cover photo (left) + meta panel (right) */}
                <section className="ee-main-grid">
                    {/* Cover photo card */}
                    <div className="ee-card ee-cover-card">
                        <div className="ee-card__header">
                            <div className="ee-card__heading">
                                <ImageOutlinedIcon className="ee-card__icon" />
                                <h3 className="ee-card__title">Cover photo</h3>
                            </div>
                            {coverPicOriginalUrl && (
                                <FancyIconButton
                                    ariaLabel="Remove cover photo"
                                    size="sm"
                                    shape="square"
                                    variant="ghost"
                                    onClick={clearCoverPic}
                                >
                                    <CloseIcon fontSize="small" />
                                </FancyIconButton>
                            )}
                        </div>

                        <div
                            className={`ee-cover-area ${isDragging ? 'is-dragover' : ''} ${coverPicOriginalUrl ? 'has-image' : 'is-empty'}`}
                            onDragOver={(e) => { e.preventDefault(); if(!isDragging) setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={onDropCover}
                            style={{backgroundColor: coverPicOriginalUrl ? coverBackgroundColor : undefined}}
                        >
                            {coverPicOriginalUrl ? (
                                <Cropper
                                    image={coverPicOriginalUrl}
                                    crop={coverCrop}
                                    zoom={coverZoom}
                                    aspect={COVER_ASPECT}
                                    minZoom={COVER_MIN_ZOOM}
                                    maxZoom={COVER_MAX_ZOOM}
                                    restrictPosition={false}
                                    showGrid={false}
                                    onCropChange={onCoverCropChange}
                                    onCropComplete={onCoverCropComplete}
                                    onZoomChange={onCoverZoomChange}
                                />
                            ) : (
                                <button
                                    type="button"
                                    className="ee-cover-empty"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <PhotoCameraOutlinedIcon className="ee-cover-empty__icon" />
                                    <div className="ee-cover-empty__title">Drop, paste, or click</div>
                                    <div className="ee-cover-empty__sub">Drag a file, ⌘V from clipboard, or upload</div>
                                </button>
                            )}
                        </div>

                        {/* Inline cover suggestions strip */}
                        {coverSuggestions.length > 0 && (
                            <div className="ee-cover-suggestions">
                                <div className="ee-cover-suggestions__label">Or pick from this event</div>
                                <div className="ee-cover-suggestions__strip">
                                    {coverSuggestions.map((url, idx) => (
                                        <button
                                            key={`${url}-${idx}`}
                                            type="button"
                                            className={`ee-cover-suggestions__thumb ${url === coverPicOriginalUrl ? 'is-active' : ''}`}
                                            onClick={() => onSelectFromEventPhotos(url)}
                                            aria-label={`Use event photo ${idx+1} as cover`}
                                        >
                                            <img src={url} alt={`Event ${idx+1}`} />
                                        </button>
                                    ))}
                                    {hasMoreCoverOptions && (
                                        <button
                                            type="button"
                                            className="ee-cover-suggestions__more"
                                            onClick={(e) => setEventPhotoMenuAnchor(e.currentTarget)}
                                        >
                                            +{descriptionImageUrls.length - COVER_THUMB_LIMIT}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="ee-cover-actions">
                            <FancyButton
                                onClick={() => fileInputRef.current?.click()}
                                loading={coverUploadStatus === 'uploading'}
                                disabled={isSaving}
                                startIcon={<PhotoCameraOutlinedIcon fontSize="small" />}
                                fullWidth
                            >
                                Upload
                            </FancyButton>
                            <FancyButton
                                onClick={(e) => setEventPhotoMenuAnchor(e.currentTarget)}
                                disabled={isSaving || descriptionImageUrls.length === 0}
                                startIcon={<CollectionsOutlinedIcon fontSize="small" />}
                                variant="secondary"
                                fullWidth
                            >
                                {descriptionImageUrls.length > 0
                                    ? `All photos (${descriptionImageUrls.length})`
                                    : 'All photos'}
                            </FancyButton>
                        </div>

                        <Popover
                            open={Boolean(eventPhotoMenuAnchor)}
                            anchorEl={eventPhotoMenuAnchor}
                            onClose={() => setEventPhotoMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <div className="ee-photo-grid">
                                {descriptionImageUrls.map((url, idx) => (
                                    <button
                                        key={`${url}-${idx}`}
                                        type="button"
                                        onClick={() => onSelectFromEventPhotos(url)}
                                        className="ee-photo-grid__item"
                                        aria-label={`Use event photo ${idx+1} as cover`}
                                    >
                                        <img src={url} alt={`Event ${idx+1}`} />
                                    </button>
                                ))}
                            </div>
                        </Popover>

                        {coverPicOriginalUrl && (
                            <div className="ee-cover-tools">
                                <div className="ee-cover-tool">
                                    <div className="ee-cover-tool__row">
                                        <span className="ee-cover-tool__label">Zoom</span>
                                        <span className="ee-cover-tool__value">{Math.round(coverZoom * 100)}%</span>
                                    </div>
                                    <div className="ee-cover-zoom">
                                        <FancyIconButton
                                            ariaLabel="Zoom out"
                                            size="sm"
                                            shape="square"
                                            variant="secondary"
                                            disabled={numbersAreClose(coverZoom, COVER_MIN_ZOOM)}
                                            onClick={() => adjustCoverZoom(-COVER_ZOOM_STEP)}
                                        >
                                            <RemoveIcon fontSize="small"/>
                                        </FancyIconButton>
                                        <Slider
                                            className="ee-cover-zoom__slider"
                                            min={COVER_MIN_ZOOM}
                                            max={COVER_MAX_ZOOM}
                                            step={COVER_ZOOM_STEP}
                                            value={coverZoom}
                                            onChange={(_, value) => onCoverZoomChange(value)}
                                            aria-label="Cover photo zoom"
                                        />
                                        <FancyIconButton
                                            ariaLabel="Zoom in"
                                            size="sm"
                                            shape="square"
                                            variant="secondary"
                                            disabled={numbersAreClose(coverZoom, COVER_MAX_ZOOM)}
                                            onClick={() => adjustCoverZoom(COVER_ZOOM_STEP)}
                                        >
                                            <AddIcon fontSize="small"/>
                                        </FancyIconButton>
                                        <FancyIconButton
                                            ariaLabel="Reset crop"
                                            size="sm"
                                            shape="square"
                                            variant="ghost"
                                            onClick={resetCoverCrop}
                                        >
                                            <RestartAltIcon fontSize="small"/>
                                        </FancyIconButton>
                                    </div>
                                </div>

                                <div className="ee-cover-tool">
                                    <button
                                        type="button"
                                        className="ee-cover-tool__row ee-cover-tool__toggle"
                                        onClick={() => setShowBackgroundPicker((v) => !v)}
                                        aria-expanded={showBackgroundPicker}
                                    >
                                        <span className="ee-cover-tool__label">Background fill</span>
                                        <span className="ee-cover-tool__swatch-row">
                                            <span className="ee-cover-tool__swatch" style={{backgroundColor: coverBackgroundColor}}/>
                                            <span className="ee-cover-tool__chev" data-open={showBackgroundPicker}>▾</span>
                                        </span>
                                    </button>
                                    {showBackgroundPicker && (
                                        <div className="ee-cover-tool__picker">
                                            {coverDominantColors.length > 0 && (
                                                <div className="ee-color-presets">
                                                    <div className="ee-color-presets__label">From your photo</div>
                                                    <div className="ee-color-presets__row">
                                                        {coverDominantColors.map((c) => (
                                                            <button
                                                                key={c}
                                                                type="button"
                                                                className={`ee-color-preset ${c.toLowerCase() === coverBackgroundColor.toLowerCase() ? 'is-active' : ''}`}
                                                                style={{backgroundColor: c}}
                                                                onClick={() => {
                                                                    setCoverBackgroundColor(c);
                                                                    setCoverDirty(true);
                                                                    markDirty();
                                                                }}
                                                                aria-label={`Use ${c}`}
                                                                title={c}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <HexColorPicker
                                                color={coverBackgroundColor}
                                                onChange={(color) => {
                                                    setCoverBackgroundColor(color);
                                                    setCoverDirty(true);
                                                    markDirty();
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Meta panel: When / Where / People & Tags */}
                    <div className="ee-card ee-meta-card">
                        <div className="ee-meta-section">
                            <div className="ee-meta-section__head">
                                <div className="ee-card__heading">
                                    <EventOutlinedIcon className="ee-card__icon" />
                                    <h3 className="ee-card__title">When</h3>
                                </div>
                                <label className="ee-allday">
                                    <Switch
                                        size="small"
                                        checked={isAllDay}
                                        onChange={(_, checked) => toggleAllDay(checked)}
                                    />
                                    <span>All-day</span>
                                </label>
                            </div>

                            {/* Quick-pick chips */}
                            <div className="ee-quick-chips" role="group" aria-label="Quick date">
                                <button type="button" className="ee-chip" onClick={setQuickToday}>Today</button>
                                <button type="button" className="ee-chip" onClick={setQuickYesterday}>Yesterday</button>
                                <button type="button" className="ee-chip" onClick={setQuickWeekend}>This weekend</button>
                            </div>

                            <div className="ee-when-row">
                                <MyDateTimePicker
                                    label={isAllDay ? 'Start date' : 'Starts'}
                                    date={startDate}
                                    time={startTime}
                                    includeTime={!isAllDay}
                                    setValue={onStartChange}
                                    onError={(hasError) => setPickerErrors({...pickerErrors, start: hasError})}
                                    error={Boolean(validationErrors.startDate)}
                                    helperText={validationErrors.startDate || ''}
                                />
                            </div>

                            {showEnd ? (
                                <>
                                    <div className="ee-when-row">
                                        <MyDateTimePicker
                                            label={isAllDay ? 'End date' : 'Ends'}
                                            date={endDate}
                                            time={endTime}
                                            includeTime={!isAllDay}
                                            setValue={onEndChange}
                                            onError={(hasError) => setPickerErrors({...pickerErrors, end: hasError})}
                                            error={Boolean(validationErrors.endDate)}
                                            helperText={validationErrors.endDate || ''}
                                        />
                                    </div>
                                    <button type="button" className="ee-link-btn" onClick={removeEnd}>
                                        Remove end
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="ee-link-btn ee-link-btn--add" onClick={() => setShowEnd(true)}>
                                    <AddRoundedIcon fontSize="small" /> Add end
                                </button>
                            )}
                        </div>

                        <div className="ee-meta-section">
                            <div className="ee-card__heading">
                                <PlaceOutlinedIcon className="ee-card__icon" />
                                <h3 className="ee-card__title">Where</h3>
                            </div>
                            <div className="ee-place">
                                <PlaceSearch
                                    value={location ? location.description : ''}
                                    setValue={(value) => {
                                        setLocation(value);
                                        markDirty();
                                        if(validationErrors.location) setValidationErrors({...validationErrors, location: null});
                                    }}
                                    locationType={location ? location.locationType : null}
                                    error={validationErrors.location}
                                />
                            </div>
                            {validationErrors.location && (
                                <div className="ee-field-error">{validationErrors.location}</div>
                            )}
                        </div>

                        {/* People & Tags — single tabbed picker */}
                        <div className="ee-meta-section">
                            <div className="ee-card__heading">
                                <PeopleAltOutlinedIcon className="ee-card__icon" />
                                <h3 className="ee-card__title">People &amp; tags</h3>
                            </div>
                            <Tabs
                                value={peopleTab}
                                onChange={(_, v) => setPeopleTab(v)}
                                className="ee-people-tabs"
                                variant="fullWidth"
                                aria-label="People and tags"
                            >
                                <Tab disableRipple label={
                                    <Badge color="primary" badgeContent={peopleCounts[0]} max={99}>
                                        <span className="ee-tab-label">Friends</span>
                                    </Badge>
                                } />
                                <Tab disableRipple label={
                                    <Badge color="primary" badgeContent={peopleCounts[1]} max={99}>
                                        <span className="ee-tab-label">Trips</span>
                                    </Badge>
                                } />
                                <Tab disableRipple label={
                                    <Badge color="primary" badgeContent={peopleCounts[2]} max={99}>
                                        <span className="ee-tab-label">Tags</span>
                                    </Badge>
                                } />
                            </Tabs>
                            <div className="ee-people-panel">
                                {peopleTab === 0 && (
                                    <FancyAutocomplete
                                        multiple
                                        limitTags={4}
                                        label="Add friends"
                                        fullWidth
                                        options={Object.keys(userFriends)}
                                        getOptionLabel={(option) => userFriends[option]?.name || 'Unavailable friend'}
                                        value={friendIds}
                                        error={Boolean(validationErrors.friends)}
                                        helperText={validationErrors.friends || ''}
                                        onChange={(newValue) => {
                                            setFriendIds(newValue);
                                            markDirty();
                                            if(validationErrors.friends) setValidationErrors({...validationErrors, friends: null});
                                        }}
                                    />
                                )}
                                {peopleTab === 1 && (
                                    <FancyAutocomplete
                                        multiple
                                        limitTags={4}
                                        label="Add trips"
                                        fullWidth
                                        options={tripOptions}
                                        getOptionLabel={(option) => userTrips[option]?.name || 'Unavailable trip'}
                                        value={tripIds}
                                        error={Boolean(validationErrors.trips)}
                                        helperText={validationErrors.trips || ''}
                                        onChange={(newValue) => {
                                            setTripIds(newValue);
                                            markDirty();
                                            if(validationErrors.trips) setValidationErrors({...validationErrors, trips: null});
                                        }}
                                    />
                                )}
                                {peopleTab === 2 && (
                                    <FancyAutocomplete
                                        multiple
                                        limitTags={4}
                                        label="Add tags"
                                        fullWidth
                                        options={Object.keys(userTags)}
                                        getOptionLabel={(option) => userTags[option]?.name || 'Unavailable tag'}
                                        value={tagIds}
                                        error={Boolean(validationErrors.tags)}
                                        helperText={validationErrors.tags || ''}
                                        onChange={(newValue) => {
                                            setTagIds(newValue);
                                            markDirty();
                                            if(validationErrors.tags) setValidationErrors({...validationErrors, tags: null});
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Description (full width) */}
                <section className="ee-card ee-description-card">
                    <div className="ee-card__header">
                        <div className="ee-card__heading">
                            <NotesOutlinedIcon className="ee-card__icon" />
                            <h3 className="ee-card__title">Description</h3>
                        </div>
                        <span className="ee-card__hint">Photos here can become the cover</span>
                    </div>
                    <div className="ee-description-editor">
                        <RichTextEditor
                            readonly={false}
                            contentHTML={descriptionHTML}
                            setContentHTML={(v) => { setDescriptionHTML(v); markDirty(); }}
                            setContentJSON={setDescriptionJSON}
                            onUploadStateChange={(hasActiveUploads) => {
                                setHasDescriptionUploads(hasActiveUploads);
                                if(!hasActiveUploads && validationErrors.uploads){
                                    setValidationErrors({...validationErrors, uploads: null});
                                }
                            }}
                        />
                    </div>
                </section>
            </div>

            {/* ───────── Sticky bottom bar ───────── */}
            <footer className="ee-bottombar">
                <div className="ee-bottombar__left">
                    {dialogData.event.id && !confirmingDelete && (
                        <FancyButton
                            variant="danger"
                            onClick={() => setConfirmingDelete(true)}
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                        >
                            Delete event
                        </FancyButton>
                    )}
                    {dialogData.event.id && confirmingDelete && (
                        <div className="ee-delete-confirm" role="alertdialog" aria-label="Confirm delete">
                            <span className="ee-delete-confirm__msg">Permanently delete this event?</span>
                            <FancyButton variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                                Cancel
                            </FancyButton>
                            <FancyButton variant="danger" size="sm" onClick={performDelete}>
                                Yes, delete
                            </FancyButton>
                        </div>
                    )}
                </div>
                <div className="ee-bottombar__right">
                    {uploadBlockMessage && (
                        <span className="ee-bottombar__status ee-bottombar__status--warn">
                            {uploadBlockMessage}
                        </span>
                    )}
                </div>
            </footer>

            <input
                onChange={onSelectCoverPic}
                multiple={false}
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
            />
        </div>
    );
}
